import type { JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AvatarFrame } from '../shared/components/AvatarFrame.js';
import type { GameEndPlayer, GameEndView } from './types.js';
import '../shared/components/CastGrid.css';
import '../discussion/components/CastGrid.css';
import './GhostVoidScene.css';

interface GhostVoidSceneProps {
  view: GameEndView;
  onBackToLobby: () => void;
}

/**
 * The ghost-win resting state — full-bleed cool Spectral atmosphere, the
 * "ghostly presence has taken the room" the brief calls out. Cool counterpart
 * to the stalemate Lights-Out (which is the warm-ink inversion); here the
 * inversion is COOL Spectral (the otherworldly side prevailed, the room is
 * cold). No book — the book was consumed by `GameEndBurn`; what's left is the
 * void and the cast unmask floating in it.
 *
 * Hierarchy mirrors the villager curtain call inverted: the winning Ghost(s)
 * foregrounded as the larger portraits with the Spectral manifestation halo
 * (the otherworldly identity, now triumphant); the losing village + fools
 * unmasked beneath as the secondary text+portrait, recessed. Verdict at the
 * head, cast in the body, word + host action at the foot.
 *
 * Word gate: shown in every ghost-win EXCEPT a correct guess (GuessReveal
 * already revealed the secret word; re-showing is redundant). The view's
 * `word.shown` already encodes this.
 */
export function GhostVoidScene({ view, onBackToLobby }: GhostVoidSceneProps): JSX.Element {
  const { t } = useTranslation();
  const winners = view.cast.filter((p) => p.won);
  const losers = view.cast.filter((p) => !p.won);

  return (
    <section className="susdle-ghost-void" aria-label={t('gameEnd.ariaLabel')}>
      <div className="susdle-ghost-void__inner">
        <h1 className="susdle-ghost-void__title">{t('gameEnd.verdict.ghosts')}</h1>
        <p className="susdle-ghost-void__reason">
          {t(`gameEnd.reason.${view.outcome}`)}
        </p>

        {/* Winners — the Ghost(s) — foregrounded. Reuses the shared cast grid
            geometry; the cool palette + manifestation halo come from
            GhostVoidScene.css. */}
        <ul className="susdle-cast-grid susdle-ghost-void__cast" aria-label={t('gameEnd.curtain.winnersAria')}>
          {winners.map((p) => (
            <VoidCell key={p.id} player={p} />
          ))}
        </ul>

        {losers.length > 0 ? (
          <>
            <div className="susdle-ghost-void__rule" aria-hidden />
            <ul
              className="susdle-cast-grid susdle-ghost-void__cast susdle-ghost-void__cast--lose"
              aria-label={t('gameEnd.curtain.losersAria')}
            >
              {losers.map((p) => (
                <VoidCell key={p.id} player={p} />
              ))}
            </ul>
          </>
        ) : null}

        {/* Word — shown for parity and other no-guess endings; suppressed for
            correct-guess ghost win (view.word.shown already enforces this). */}
        {view.word.shown ? (
          <p className="susdle-ghost-void__word">
            <Trans
              i18nKey="gameEnd.word.shown"
              values={{ word: view.word.word.toUpperCase() }}
              components={[<span className="susdle-ghost-void__word-mark" key="w" />]}
            />
          </p>
        ) : null}

        {view.isHost ? (
          <button
            type="button"
            className="susdle-ghost-void__begin"
            onClick={onBackToLobby}
          >
            {t('gameEnd.action.begin')}
          </button>
        ) : (
          <p className="susdle-ghost-void__waiting">
            {t('gameEnd.action.waiting', { host: view.hostName })}
          </p>
        )}
      </div>
    </section>
  );
}

function VoidCell({ player }: { player: GameEndPlayer }): JSX.Element {
  const { t } = useTranslation();
  return (
    <li>
      <span
        className="susdle-cast-cell"
        data-static="yes"
        data-eliminated={player.eliminated ? 'yes' : 'no'}
        data-role={player.role}
      >
        <AvatarFrame player={player} isHost={false} suppressEntry />
        <span className="susdle-ghost-void__role">{t(`gameEnd.role.${player.role}`)}</span>
      </span>
    </li>
  );
}
